/**
 * Regex patterns for identifiers, exported as strings for use in Router paths
 */
export const CPF_REGEX = "\\d{11}|\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2}";
export const CNPJ_BASE_REGEX = "[A-Za-z0-9]{8}|[A-Za-z0-9]{2}\\.[A-Za-z0-9]{3}\\.[A-Za-z0-9]{3}";
export const CNPJ_FULL_REGEX = "[A-Za-z0-9]{12}\\d{2}|[A-Za-z0-9]{2}\\.[A-Za-z0-9]{3}\\.[A-Za-z0-9]{3}/[A-Za-z0-9]{4}-\\d{2}";
export const CNPJ_ANY_REGEX = `${CNPJ_FULL_REGEX}|${CNPJ_BASE_REGEX}`;
export const UUID_REGEX = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

export const isValidUuid = (value: string | undefined): boolean => {
  if (!value) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

/**
 * Validates if a string follows a CPF pattern (11 digits or 000.000.000-00)
 */
export const isCpfPattern = (value: string | undefined): boolean => {
  if (!value) return false;
  return new RegExp(`^(${CPF_REGEX})$`).test(value);
};

/**
 * Validates if a string follows a CNPJ pattern - is case insensitive to allow users to enter lowercase letters.
 * Supports:
 * - CNPJ8: 8 characters or letters or  00.000.000
 * - CNPJ14: 14 characters or letters or 00.000.000/0000-00
 */
export const isCnpjPattern = (value: string | undefined, type?: 'base' | 'full'): boolean => {
  if (!value) return false;
  
  if (type === 'base') return new RegExp(`^(${CNPJ_BASE_REGEX})$`).test(value);
  if (type === 'full') return new RegExp(`^(${CNPJ_FULL_REGEX})$`).test(value);
  
  return new RegExp(`^(${CNPJ_ANY_REGEX})$`).test(value);
};

/**
 * Checks if the string is specifically a formatted variant
 */
export const isFormattedIdentifier = (value: string | undefined): boolean => {
  if (!value) return false;
  return /[./-]/.test(value);
};

// Legacy support if needed elsewhere
export const isValidCpf = (cpf: string | undefined): boolean => isCpfPattern(cpf);
export const isValidCnpj = (cnpj: string | undefined): boolean => isCnpjPattern(cnpj);

// --- Shape validation -----------------------------------------------------------------------
// Shape/length only — no check-digit verification (an all-zeros CPF/CNPJ is accepted). Reuses
// the `*Pattern` helpers above; input may be formatted or raw. The 14-char branch accepts the
// alphanumeric CNPJ (Serpro/RFB, effective Jul/2026) since `CNPJ_FULL_REGEX` allows [A-Za-z0-9]
// in the base positions.

/**
 * True when `value` looks like a CPF (11 digits) or a full CNPJ (14, alphanumeric) by shape.
 * Empty is treated as valid (absence is an emptiness concern, not a validity one) — callers
 * gate required-ness separately.
 */
export const isValidBrTaxId = (value: string | null | undefined): boolean => {
    const cleaned = String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!cleaned) return true;
    if (cleaned.length === 11) return isCpfPattern(cleaned);
    if (cleaned.length === 14) return isCnpjPattern(cleaned, 'full');
    return false;
};
